import { useState, useMemo, useEffect } from 'react'
import EventDetailPanel from '../components/EventDetailPanel'
import { useGlobalActivity } from '../hooks/useGlobalActivity'
import { useGlobalMetrics } from '../hooks/useGlobalMetrics'
import { supabase } from '../../../shared/lib/supabase'

const ACTION_COLORS = {
  'user.login': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'user.logout': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400',
  'login.success': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'login.failed': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  'session.idle_timeout': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  'mfa.enrolled': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'mfa.verified': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'mfa.reverify': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  'password.changed': 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  'client.created': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  'client.updated': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  'assessment.started': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  'assessment.completed': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  'policy.generated': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  'user.invited': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400',
  'user.deleted': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400',
  'org.settings_updated': 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
}

const ACTION_TYPES = [
  { value: '', label: 'All Actions' },
  { value: 'user.login', label: 'Login' },
  { value: 'session.idle_timeout', label: 'Session Timeout' },
  { value: 'mfa.enrolled', label: 'MFA Enrolled' },
  { value: 'mfa.verified', label: 'MFA Verified' },
  { value: 'password.changed', label: 'Password Changed' },
  { value: 'client.created', label: 'Client Created' },
  { value: 'client.updated', label: 'Client Updated' },
  { value: 'assessment.started', label: 'Assessment Started' },
  { value: 'assessment.completed', label: 'Assessment Completed' },
  { value: 'policy.generated', label: 'Policy Generated' },
  { value: 'user.invited', label: 'User Invited' },
  { value: 'user.deleted', label: 'User Deleted' }
]

function GlobalActivityLog() {
  const [orgFilter, setOrgFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [page, setPage] = useState(0)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const limit = 50

  const { activities: orgActivities, totalCount, loading, refresh, lastRefreshed } = useGlobalActivity({
    orgId: orgFilter || undefined,
    action: actionFilter || undefined,
    limit,
    offset: page * limit
  })

  // Fetch all audit_logs events (login, MFA, session, changes) for Global Admin view
  const [auditEvents, setAuditEvents] = useState([])
  useEffect(() => {
    const since = new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString()
    supabase
      .from('audit_logs')
      .select('id, user_id, user_email, user_role, action, description, category, client_id, client_name, resource_type, resource_id, created_at, metadata')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data }) => {
        setAuditEvents((data || []).map(e => ({
          ...e,
          org_name: e.client_name || null,
          resource_type: e.resource_type || e.category,
          source: 'audit_logs'
        })))
      })
  }, [lastRefreshed])

  // Merge org activity_log events with all audit_log events
  const activities = useMemo(() => {
    let merged = auditEvents

    // If filtering by org, only show activity_log entries for that org
    // (audit_logs don't have org_id, so they're excluded when org filter is active)
    if (orgFilter) {
      return orgActivities
    }

    // If filtering by action, filter the merged set
    if (actionFilter) {
      const orgFiltered = orgActivities.filter(e => e.action === actionFilter)
      const auditFiltered = auditEvents.filter(e => e.action === actionFilter)
      merged = [...orgFiltered, ...auditFiltered]
    } else {
      merged = [...orgActivities, ...auditEvents]
    }

    // Deduplicate by id, sort by date
    const seen = new Set()
    return merged
      .filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }, [orgActivities, auditEvents, orgFilter, actionFilter])

  const { metrics } = useGlobalMetrics()
  const orgOptions = useMemo(() => {
    return (metrics?.orgs || []).map(o => ({ value: o.org_id, label: o.org_name }))
  }, [metrics])

  const totalPages = Math.ceil(totalCount / limit)

  const exportCSV = () => {
    const headers = ['Timestamp', 'Organization', 'User', 'Action', 'Resource Type', 'Resource ID']
    const rows = activities.map(a => [
      new Date(a.created_at).toISOString(),
      a.org_name || '',
      a.user_email || '',
      a.action,
      a.resource_type || '',
      a.resource_id || ''
    ])
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Activity & Security</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Platform-wide activity, authentication, and security events</p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefreshed && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Updated {lastRefreshed.toLocaleTimeString()} · auto-refreshes every 5 min
            </span>
          )}
          <button
            onClick={refresh}
            className="btn-secondary flex items-center gap-1.5"
            title="Refresh now"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <button onClick={exportCSV} className="btn-secondary">
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Organization</label>
          <select
            value={orgFilter}
            onChange={(e) => { setOrgFilter(e.target.value); setPage(0) }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Organizations</option>
            {orgOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Action Type</label>
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0) }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {ACTION_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <span className="text-sm text-gray-500 dark:text-gray-400">{totalCount} total entries</span>
        </div>
      </div>

      {/* Activity Table */}
      <div className="card p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Resource</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {activities.map(activity => (
                    <tr
                      key={activity.id}
                      className={`cursor-pointer transition-colors ${selectedEvent?.id === activity.id ? 'bg-primary-50 dark:bg-primary-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      onClick={() => setSelectedEvent(selectedEvent?.id === activity.id ? null : activity)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(activity.created_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-white">{activity.org_name || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-white">{activity.user_email || '-'}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          ACTION_COLORS[activity.action] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                        }`}>
                          {activity.action}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {activity.resource_type ? `${activity.resource_type}` : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {activities.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No activity found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <EventDetailPanel event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  )
}

export default GlobalActivityLog
